import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import Hero from '../components/Hero';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faQuestionCircle, faEnvelope, faPhone } from '@fortawesome/free-solid-svg-icons';
import './Support.css';

// Import images
import destekHeroImage from '../assets/images/destek.jpg';

const Support = () => {
  const faqs = [
    {
      id: 1,
      question: 'ProgenX Akademi nedir?',
      answer: 'ProgenX Akademi, yapay zeka destekli kişiselleştirilmiş öğrenme deneyimi sunan, öğrencilerin bireysel öğrenme ihtiyaçlarına göre optimize edilmiş eğitim programları oluşturan yenilikçi bir eğitim platformudur.'
    },
    {
      id: 2,
      question: 'Hangi cihazlardan erişebilirim?',
      answer: 'ProgenX Akademi\'ye bilgisayar, tablet ve akıllı telefonlar üzerinden erişebilirsiniz. Web tarayıcısı üzerinden veya mobil uygulama aracılığıyla kullanabilirsiniz.'
    },
    {
      id: 3,
      question: 'Ödeme seçenekleri nelerdir?',
      answer: 'Kredi kartı, banka kartı veya havale/EFT ile ödeme yapabilirsiniz. Ayrıca kurumsal anlaşmalar için farklı ödeme seçenekleri de sunulmaktadır.'
    },
    {
      id: 4,
      question: 'İçerikler hangi yaş gruplarına uygundur?',
      answer: 'İçeriklerimiz ilkokul, ortaokul ve lise düzeyindeki öğrenciler için hazırlanmıştır. Her seviyeye uygun özelleştirilmiş içerikler bulunmaktadır.'
    },
    {
      id: 5,
      question: 'Şifremi unuttum, ne yapmalıyım?',
      answer: 'Giriş sayfasında yer alan "Şifremi Unuttum" bağlantısına tıklayarak e-posta adresinize sıfırlama bağlantısı gönderilmesini sağlayabilirsiniz.'
    }
  ];

  const SupportCard = ({ icon, title, description, buttonText, buttonLink, isExternal }) => {
    const ButtonComponent = isExternal ? 'a' : Link;
    const buttonProps = isExternal 
      ? { href: buttonLink, target: '_blank', rel: 'noopener noreferrer' }
      : { to: buttonLink };
    
    return (
      <div className="support-card">
        <FontAwesomeIcon icon={icon} className="support-card-icon" />
        <h3 className="support-card-title">{title}</h3>
        <p className="support-card-description">{description}</p>
        <ButtonComponent {...buttonProps} className="btn btn-primary">
          {buttonText}
        </ButtonComponent>
      </div>
    );
  };

  return (
    <div className="support">
      <Helmet>
        <title>ProgenX Destek | Yardım Merkezi ve Sıkça Sorulan Sorular 2025</title>
        <meta name="description" content="ProgenX destek sayfası. Sıkça sorulan sorular, e-posta ve telefon desteği. ProgenX Akademi platformu kullanımı, abonelikler, teknik sorunlar ve diğer konularda yardım alın. 7/24 müşteri desteği." />
        <meta name="keywords" content="progenx destek, progenx yardım, progenx sss, progenx sorun çözme, progenx müşteri hizmetleri, progenx iletişim, progenx teknik destek, progenx kullanım kılavuzu" />
        <meta property="og:title" content="ProgenX Destek | Yardım Merkezi" />
        <meta property="og:description" content="ProgenX kullanıcıları için kapsamlı destek merkezi. Sıkça sorulan sorular ve iletişim seçenekleri." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://progenxacademy.com/destek" />
        <link rel="canonical" href="https://progenxacademy.com/destek" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                "name": "ProgenX Akademi nedir?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "ProgenX Akademi, yapay zeka destekli kişiselleştirilmiş öğrenme deneyimi sunan, öğrencilerin bireysel öğrenme ihtiyaçlarına göre optimize edilmiş eğitim programları oluşturan yenilikçi bir eğitim platformudur."
                }
              },
              {
                "@type": "Question",
                "name": "Hangi cihazlardan erişebilirim?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "ProgenX Akademi'ye bilgisayar, tablet ve akıllı telefonlar üzerinden erişebilirsiniz. Web tarayıcısı üzerinden veya mobil uygulama aracılığıyla kullanabilirsiniz."
                }
              },
              {
                "@type": "Question",
                "name": "Ödeme seçenekleri nelerdir?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Kredi kartı, banka kartı veya havale/EFT ile ödeme yapabilirsiniz. Ayrıca kurumsal anlaşmalar için farklı ödeme seçenekleri de sunulmaktadır."
                }
              }
            ]
          })}
        </script>
      </Helmet>
      
      <Hero 
        title="DESTEK" 
        backgroundImage={destekHeroImage}
      />
      
      <section className="support-content">
        <div className="container">
          <h2 className="section-title">Size Nasıl Yardımcı Olabiliriz?</h2>
          <p className="support-intro">
            ProgenX Akademi kullanıcılarımıza en iyi destek hizmetini sunmak için buradayız. Aşağıdaki seçeneklerden ihtiyacınıza uygun olanı seçebilirsiniz.
          </p>
          
          <div className="support-cards">
            <SupportCard 
              icon={faQuestionCircle}
              title="Sıkça Sorulan Sorular"
              description="Sık sorulan sorular ve cevapları ile hızlıca çözüm bulun"
              buttonText="SSS'lere Göz Atın"
              buttonLink="#faq-section"
            />
            
            <SupportCard 
              icon={faEnvelope}
              title="E-posta Desteği"
              description="Sorularınız için bize e-posta gönderin, en kısa sürede yanıtlayalım"
              buttonText="E-posta Gönder"
              buttonLink="/iletisim"
            />
            
            <SupportCard 
              icon={faPhone}
              title="Telefon Desteği"
              description="Hafta içi 09:00-18:00 saatleri arasında bize ulaşabilirsiniz"
              buttonText="Bizi Arayın"
              buttonLink="tel:+905386864516"
              isExternal={true}
            />
          </div>
          
          <section id="faq-section" className="faq-section">
            <h3 className="faq-section-title">Sıkça Sorulan Sorular</h3>
            
            <div className="faq-list">
              {faqs.map(faq => (
                <div key={faq.id} className="faq-item">
                  <h4 className="faq-question">
                    <FontAwesomeIcon icon={faQuestionCircle} className="faq-question-icon" />
                    {faq.question}
                  </h4>
                  <p className="faq-answer">{faq.answer}</p>
                </div>
              ))}
            </div>
          </section>
          
          <div className="support-cta">
            <h3 className="support-cta-title">Sorunuzu Bulamadınız mı?</h3>
            <p className="support-cta-text">Daha kapsamlı destek için bizimle iletişime geçebilirsiniz.</p>
            <Link to="/iletisim" className="btn btn-primary">İLETİŞİME GEÇ</Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Support; 